/**
 * Bedrock API Service
 *
 * Service functions for interacting with the Bedrock / CloudWatch integration endpoints.
 */

import { apiClient } from "./api";
import type {
  BedrockConnectionCreate,
  BedrockConnectionRead,
  BedrockSetupInstructions,
  BedrockSyncResult,
  PaginatedBedrockConnections,
} from "@/types/bedrock";

const API_PREFIX = "/api/v1/bedrock";

/**
 * Fetch a generated external_id and IAM policy templates for onboarding a new connection.
 * Call this before showing the "Add Connection" form so the user can configure their IAM role first.
 */
export async function getBedrockSetupInstructions(): Promise<BedrockSetupInstructions> {
  return apiClient.get<BedrockSetupInstructions>(`${API_PREFIX}/connections/setup`);
}

/**
 * List all Bedrock connections for the caller's organisation.
 */
export async function listBedrockConnections(
  page: number = 1,
  itemsPerPage: number = 10
): Promise<PaginatedBedrockConnections> {
  const params = new URLSearchParams({
    page: page.toString(),
    items_per_page: itemsPerPage.toString(),
  });
  return apiClient.get<PaginatedBedrockConnections>(
    `${API_PREFIX}/connections?${params.toString()}`
  );
}

/**
 * Create and validate a new Bedrock connection.
 * The server will immediately attempt STS AssumeRole + DescribeLogGroups.
 */
export async function createBedrockConnection(
  data: BedrockConnectionCreate
): Promise<BedrockConnectionRead> {
  return apiClient.post<BedrockConnectionRead>(`${API_PREFIX}/connections`, data);
}

/**
 * Delete a Bedrock connection by ID.
 */
export async function deleteBedrockConnection(connectionId: number): Promise<void> {
  return apiClient.delete(`${API_PREFIX}/connections/${connectionId}`);
}

/**
 * Manually trigger an immediate data pull for a connection.
 */
export async function syncBedrockConnection(connectionId: number): Promise<BedrockSyncResult> {
  return apiClient.post<BedrockSyncResult>(
    `${API_PREFIX}/connections/${connectionId}/sync`,
    {}
  );
}
